import type { InferContractRouterOutputs } from '@orpc/contract'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { useMemo, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { twJoin } from 'tailwind-merge'
import { withQuery } from 'ufo'
import IcBaselineGridView from '~icons/ic/baseline-grid-view'
import { StyleSelectDialog } from 'src/components/GenerationTask/MultiColumnGenerationForm/Common/StyleSelectDialog'
import { encodeInitialValuesForImport } from 'src/components/GenerationTask/MultiColumnGenerationForm/importEncode'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialogContent,
  UIDialogFooter,
  UIDialogHeader,
  UIDialogResponsive,
} from 'src/components/ui/Modal'
import type { GenerationModelVersion } from 'src/services/apollo/generated/graphql'
import { useModelStyleKeys } from 'src/services/orpc/modelStyle'
import { getMediaUrl } from 'src/utils/media'
import { canModelUseStyleKey } from 'src/utils/model'
import { hashCode, seededSample } from 'src/utils/styleKey'
import { startEventGroup } from 'src/utils/tracker'

type StyleKeyItem =
  InferContractRouterOutputs<BaseContract>['generationModel']['getStyleKeys'][number]

type Props = {
  version?: GenerationModelVersion
  modelType: string
}

const styleCardStyles = twJoin(
  'relative flex flex-col rounded-xl overflow-hidden cursor-pointer transition-all',
  'ring-1 ring-black/10 dark:ring-white/10 shadow-sm',
  'hover:ring-2 hover:ring-purple-400 dark:hover:ring-purple-600',
)

export default function ModelStyleKeyDisplay({ version, modelType }: Props) {
  const { t } = useTranslation(['market', 'generation'])
  const navigate = useNavigate()
  const [showcaseStyleId, setShowcaseStyleId] = useState<string | undefined>()
  const [exploreDialogOpen, setExploreDialogOpen] = useState(false)

  const hasStyleKey = canModelUseStyleKey(version?.extra)
  const { data: styleKeys } = useModelStyleKeys(version?.id, hasStyleKey)

  const showcaseStyle = useMemo(
    () => styleKeys?.find(s => s.id === showcaseStyleId),
    [styleKeys, showcaseStyleId],
  )

  // Pick 6 random styles (stable per styleKeys array)
  const randomStyles = useMemo(() => {
    if (!styleKeys || styleKeys.length === 0) return []
    return seededSample(styleKeys, hashCode(styleKeys[0].id), 6)
  }, [styleKeys])

  const handleUseStyle = (styleKeyId: string) => {
    if (!version?.id) return
    startEventGroup('generate_source')
    const initialValues = encodeInitialValuesForImport({
      modelId: version.id,
      modelStyle: { type: 'key', keyId: styleKeyId },
    })
    navigate(withQuery('/generator/image', { initialValues }))
  }

  if (!hasStyleKey || !styleKeys || styleKeys.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">
        {t('market:model.built-in-styles.label')}
      </h3>

      {/* Style grid - show 6 random styles */}
      <div className="grid grid-cols-3 gap-2">
        {randomStyles.map(item => (
          <StyleCard
            key={item.id}
            item={item}
            modelType={modelType}
            onPress={() => setShowcaseStyleId(item.id)}
          />
        ))}
      </div>

      {/* Explore button */}
      <AriaButton
        onPress={() => setExploreDialogOpen(true)}
        className="flex items-center justify-center gap-1 text-xs text-center py-2 rounded-md text-neutral-500 dark:text-neutral-400 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <IcBaselineGridView className="size-4" />
        {t('generation:style.selector.browse-all')}
      </AriaButton>

      {/* Showcase dialog */}
      <UIDialogResponsive
        open={!!showcaseStyle}
        onClose={() => setShowcaseStyleId(undefined)}
        dismissable
        className="w-screen mmd:max-w-[500px]"
      >
        {showcaseStyle && (
          <>
            <UIDialogHeader
              heading={t([
                `generation:model-style.${modelType}.${showcaseStyle.id}`,
                showcaseStyle.id,
              ])}
            />
            <UIDialogContent className="overflow-auto mmd:max-h-[70dvh]">
              <div className="columns-2 gap-2">
                {showcaseStyle.artworks
                  .filter(a => a.mediaId)
                  .map(artwork => (
                    <a
                      key={artwork.artworkId}
                      href={`/artwork/${artwork.artworkId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-2 break-inside-avoid"
                    >
                      <img
                        src={getMediaUrl(artwork.mediaId, 'thumbnail')}
                        alt=""
                        className="w-full rounded-md hover:shadow-lg hover:outline outline-2 outline-purple-600 transition-all"
                      />
                    </a>
                  ))}
              </div>
            </UIDialogContent>
            <UIDialogFooter>
              <UIButton
                variant="filled"
                color="theme"
                className="w-full"
                onPress={() => {
                  setShowcaseStyleId(undefined)
                  handleUseStyle(showcaseStyle.id)
                }}
              >
                {t('generation:style.selector.use')}
              </UIButton>
            </UIDialogFooter>
          </>
        )}
      </UIDialogResponsive>

      {/* Explore dialog - same as generation panel */}
      <StyleSelectDialog
        open={exploreDialogOpen}
        onClose={() => setExploreDialogOpen(false)}
        styleKeys={styleKeys}
        modelType={modelType}
        onApply={handleUseStyle}
      />
    </div>
  )
}

/* ─── Style card ─── */

function StyleCard(props: {
  item: StyleKeyItem
  modelType: string
  onPress: () => void
}) {
  const { item, modelType, onPress } = props
  const { t } = useTranslation(['generation'])
  const coverMediaId = item.artworks[0]?.mediaId
  const label = t([`generation:model-style.${modelType}.${item.id}`, item.id])

  return (
    <AriaButton onPress={onPress} className={styleCardStyles}>
      <div className="aspect-square overflow-hidden w-full">
        {coverMediaId && (
          <img
            src={getMediaUrl(coverMediaId, 'thumbnail')}
            alt=""
            className="w-full h-full object-cover object-top"
          />
        )}
      </div>
      <div className="px-2 py-1.5 text-xs font-medium truncate w-full text-left">
        {label}
      </div>
      {item.tags.length > 0 && (
        <div className="flex gap-1 px-2 pb-1.5 overflow-hidden max-h-6">
          {item.tags.map(tag => (
            <span
              key={tag}
              className="text-[10px] px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10 text-zinc-500 dark:text-zinc-400 whitespace-nowrap shrink-0"
            >
              {t([`generation:style.tags.${tag}`, tag])}
            </span>
          ))}
        </div>
      )}
    </AriaButton>
  )
}
